import { IconButton } from "@hortis/ui/button";
import { Dialog } from "@hortis/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { HoverCard, HoverCardTrigger } from "@hortis/ui/hover-card";
import {
  DotsHorizontal,
  GitBranch1,
  RefreshCw5,
  Trash3,
} from "@hortis/ui/icons";
import { Copy1 } from "@hortis/ui/icons/copy-1";
import { FolderClosed } from "@hortis/ui/icons/folder-closed";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { toast } from "@hortis/ui/sonner";
import { Link } from "@tanstack/react-router";
import type {
  AccessionFieldsFragment,
  PlantMaterialStatus,
} from "generated/graphql";
import { useCallback, useRef, useState } from "react";
import { RestoreAccessionDialogContent } from "src/components/dialog/variants/restore-accession";
import { TrashAccessionDialog } from "src/components/dialog/variants/trash-accession";
import { DeaccessionDialogContent } from "src/features/records/accession/components/deaccession-dialog";
import { ReinstateDialogContent } from "src/features/records/accession/components/reinstate-dialog";
import { copyToClipboard } from "src/utils/dom/copy-to-clipboard";
import { useAccessToken } from "src/utils/use-access-token";
import type { UseQueryExecute } from "urql";
import {
  RecordPageHeader,
  RecordPageHeaderContent,
  RecordPageHeaderReference,
  RecordPageHeaderReferenceButton,
  RecordPageHeaderReferenceLabel,
  RecordPageHeaderTitle,
} from ".";
import * as OE from "../../utils/option-either";
import { TaxonHoverCardContent } from "../rich-text-editor/mention/taxon-hover-card-content";

type AccessionRecordPageHeaderProps = {
  accession: AccessionFieldsFragment;
  refetch: UseQueryExecute;
  canEdit: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function AccessionRecordPageHeader({
  accession,
  refetch,
  canEdit,
}: AccessionRecordPageHeaderProps) {
  const menuAnchorEl = useRef(null);

  const [deaccessionDialogOpen, setDeaccessionDialogOpen] = useState(false);

  const openDeaccessionDialog = useCallback(() => {
    setDeaccessionDialogOpen(true);
  }, []);

  const closeDeaccessionDialog = useCallback(() => {
    setDeaccessionDialogOpen(false);
  }, []);

  const [reinstateDialogOpen, setReinstateDialogOpen] = useState(false);

  const openReinstateDialog = useCallback(() => {
    setReinstateDialogOpen(true);
  }, []);

  const accessToken = OE.noneAndErrorToUndefined(useAccessToken());

  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const materialStatuses =
    accession.plantMaterial?.map(({ status }): PlantMaterialStatus => status) ??
    [];

  return (
    <RecordPageHeader>
      <RecordPageHeaderContent>
        <RecordPageHeaderTitle>
          {accession.accessionNumber}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                icon={DotsHorizontal}
                size="sm"
                className="p-1.5"
                ariaLabel="More actions"
                variant="tertiaryGray"
                testId="action-menu-button"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Copy1 className="h-4 w-4 text-grey-500" />
                  Copy
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onSelect={() => {
                        void copyToClipboard(
                          accession.accessionNumber,
                          () => {
                            toast.success(
                              `"${accession.accessionNumber}" copied to clipboard`,
                            );
                          },
                          () => {
                            //eslint-disable-next-line sonarjs/no-duplicate-string
                            toast.error("Failed to copy");
                          },
                        );
                      }}
                    >
                      <Copy1 className="h-4 w-4 text-grey-500" />
                      Copy accession number
                    </DropdownMenuItem>
                    {accession.taxon != null && (
                      <DropdownMenuItem
                        onSelect={() => {
                          void copyToClipboard(
                            accession.taxon?.scientificName ?? "",
                            () => {
                              toast.success(
                                `"${
                                  accession.taxon?.scientificName ?? ""
                                }" copied to clipboard`,
                              );
                            },
                            () => {
                              toast.error("Failed to copy");
                            },
                          );
                        }}
                      >
                        <Copy1 className="h-4 w-4 text-grey-500" />
                        Copy scientific name
                      </DropdownMenuItem>
                    )}
                    {accession.taxon?.commonName != null && (
                      <DropdownMenuItem
                        onSelect={() => {
                          void copyToClipboard(
                            accession.taxon?.commonName ?? "",
                            () => {
                              toast.success(
                                `"${
                                  accession.taxon?.commonName ?? ""
                                }" copied to clipboard`,
                              );
                            },
                            () => {
                              toast.error("Failed to copy");
                            },
                          );
                        }}
                      >
                        <Copy1 className="h-4 w-4 text-grey-500" />
                        Copy common name
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              {canEdit && (
                <>
                  {accession.deaccessionDate == null && !accession.archived && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        testId="deaccession-menu-item"
                        onClick={openDeaccessionDialog}
                      >
                        <FolderClosed className="h-4 w-4 text-grey-500" />
                        Deaccession
                      </DropdownMenuItem>
                    </>
                  )}

                  {accession.deaccessionDate != null && !accession.archived && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        testId="reinstate-menu-item"
                        onClick={openReinstateDialog}
                      >
                        <RefreshCw5 className="h-4 w-4 text-grey-500" />
                        Reinstate
                      </DropdownMenuItem>
                    </>
                  )}

                  {!accession.archived && (
                    <DropdownMenuItem
                      testId="archive-accession-menu-item"
                      onClick={() => {
                        setTrashDialogOpen(true);
                      }}
                    >
                      <Trash3 className="h-4 w-4 text-grey-500" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </RecordPageHeaderTitle>
        {accession?.taxon != null && (
          <RecordPageHeaderReference>
            <RecordPageHeaderReferenceLabel>
              Taxon
            </RecordPageHeaderReferenceLabel>
            <HoverCard>
              <HoverCardTrigger asChild>
                <RecordPageHeaderReferenceButton asChild>
                  <Link
                    to="/taxon/$taxonId"
                    params={{ taxonId: accession.taxon.id }}
                  >
                    {accession?.taxon.sharedScientificName?.taxon
                      ?.globalRedListStatus != null &&
                    accession.taxon.fullyValidated === true ? (
                      <IUCNRedListTag
                        status={
                          accession?.taxon.sharedScientificName?.taxon
                            ?.globalRedListStatus
                        }
                        short
                        className="text-xs py-px px-1 rounded-md"
                      />
                    ) : (
                      <GitBranch1 className="h-4 w-4 text-grey-500" />
                    )}
                    {accession?.scientificName}
                    <span className="text-grey-500">
                      {accession?.taxon?.commonName}
                    </span>
                  </Link>
                </RecordPageHeaderReferenceButton>
              </HoverCardTrigger>
              <TaxonHoverCardContent taxon={accession.taxon} />
            </HoverCard>
          </RecordPageHeaderReference>
        )}
      </RecordPageHeaderContent>
      {canEdit && (
        <>
          {accessToken == null ? undefined : (
            <Dialog
              open={deaccessionDialogOpen}
              onOpenChange={setDeaccessionDialogOpen}
            >
              <DeaccessionDialogContent
                accessionId={accession.id}
                accessToken={accessToken}
                materialStatuses={materialStatuses}
                open={deaccessionDialogOpen}
                refetch={refetch}
                onClose={closeDeaccessionDialog}
              />
            </Dialog>
          )}
          {accessToken != null && (
            <Dialog
              open={reinstateDialogOpen}
              onOpenChange={setReinstateDialogOpen}
            >
              <ReinstateDialogContent
                accessionId={accession.id}
                accessToken={accessToken}
                refetch={refetch}
                onOpenChange={setReinstateDialogOpen}
              />
            </Dialog>
          )}
          <TrashAccessionDialog
            open={trashDialogOpen}
            setOpen={setTrashDialogOpen}
            id={accession.id}
            onSuccess={refetch}
            triggerRef={menuAnchorEl}
          />
          <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
            <RestoreAccessionDialogContent
              id={accession.id}
              onSuccess={refetch}
            />
          </Dialog>
        </>
      )}
    </RecordPageHeader>
  );
}

export default AccessionRecordPageHeader;
